<script setup lang="ts">
import { computed } from 'vue'
import { StarIcon } from '@heroicons/vue/24/solid'
import Loader from '@/components/Globals/Feedbacks/Loader.vue'
import { AREA_TOTAL_META, type IIndicatorCard } from '@/interfaces/entities/management'
import { useManagementStore } from '@/store/management'
import CostHighlight from './CostHighlight.vue'
import GroupedCards from './GroupedCards.vue'

const store = useManagementStore()

const favorite = computed(() => store.favoriteArea)
const indicators = computed(() => (store.favoriteAreaId ? store.areaIndicators(store.favoriteAreaId) : null))

const cards = computed<IIndicatorCard[]>(() => {
  const totals = indicators.value?.totals
  if (!totals) return []
  return AREA_TOTAL_META.filter((meta) => totals[meta.key] !== null && totals[meta.key] !== undefined).map((meta) => ({
    key: meta.key,
    label: meta.label,
    unit: meta.unit,
    value: totals[meta.key] as number | null,
  }))
})

const areaCost = computed(() => {
  const totals = indicators.value?.totals
  if (!totals || totals.custo_total === null || totals.custo_total === undefined) return null
  return { total: totals.custo_total, peak: totals.custo_ponta ?? null, offpeak: totals.custo_fora_ponta ?? null }
})
</script>

<template>
  <section
    v-if="favorite"
    class="border border-amber-200 bg-amber-50/40 p-4">
    <header class="mb-3 flex items-center justify-between gap-2">
      <div class="flex items-center gap-2">
        <StarIcon class="h-5 w-5 text-amber-500" />
        <div>
          <p class="text-xs uppercase tracking-wide text-amber-600">Área favorita</p>
          <h2 class="text-lg font-bold text-slate-900">{{ favorite.name }}</h2>
        </div>
      </div>
      <span v-if="!areaCost" class="text-xs text-slate-400">
        Sem tarifa vigente — custo não calculado
      </span>
    </header>

    <Loader v-if="store.loadingIndicators && !indicators" />
    <template v-else-if="cards.length || areaCost">
      <!-- Custo em destaque, primeiro -->
      <CostHighlight
        v-if="areaCost"
        class="mb-4"
        :total="areaCost.total"
        :peak="areaCost.peak"
        :offpeak="areaCost.offpeak" />
      <GroupedCards v-if="cards.length" :cards="cards" compact />
    </template>
    <p v-else class="text-sm text-slate-500">
      Adicione sensores configurados a esta área para ver os indicadores.
    </p>
  </section>

  <!-- Sem favorita definida: dica para o usuário -->
  <section v-else class="border border-dashed border-stroke bg-white p-4 text-sm text-slate-500">
    Defina uma <strong>área favorita</strong> (botão ★ ao selecionar uma área abaixo) para que ela apareça
    aqui no topo automaticamente ao abrir o módulo.
  </section>
</template>
